import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Compass, Map, Heart, Coins } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success(t('common.success'));
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-20 pb-12 flex items-center" data-testid="login-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8 w-full">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left - Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="order-2 md:order-1"
          >
            <div className="max-w-md mx-auto md:mx-0">
              <h1 className="text-3xl md:text-4xl font-bold mb-2">{t('auth.login_title')}</h1>
              <p className="text-muted-foreground mb-8">{t('auth.login_subtitle')}</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      placeholder="vous@exemple.com"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      {t('auth.forgot_password')}
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      placeholder="••••••••"
                      required
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90 py-6"
                  disabled={loading}
                  data-testid="login-btn"
                >
                  {loading ? t('common.loading') : t('auth.login_btn')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-background text-muted-foreground">{t('auth.or')}</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full py-6"
                onClick={loginWithGoogle}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.google')}
              </Button>

              <p className="text-center mt-6 text-muted-foreground">
                {t('auth.no_account')}{' '}
                <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">
                  {t('nav.register')}
                </Link>
              </p>
            </div>
          </motion.div>

          {/* Right - Image */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="order-1 md:order-2 hidden md:block"
          >
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
              <img 
                src="https://images.unsplash.com/photo-1658642495150-9e3f52ad43a4?w=800"
                alt="Adventure"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-8">
                <div className="text-white">
                  <p className="font-handwritten text-2xl mb-2">XPerience</p>
                  <p className="text-white/80">Voyagez autrement, rencontrez vraiment</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
};

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  
  const [step, setStep] = useState(0); // 0 = choose role, 1 = form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('');
  const [guideType, setGuideType] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedCGU, setAcceptedCGU] = useState(false);
  const [acceptedCharter, setAcceptedCharter] = useState(false);

  const handleRoleSelect = (selectedRole) => {
    setRole(selectedRole);
    setStep(1); // Go directly to form for both roles
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: Confirmation mot de passe
    if (password !== confirmPassword) {
      toast.error(i18n.language === 'fr' 
        ? 'Les mots de passe ne correspondent pas' 
        : 'Passwords do not match');
      return;
    }

    // Validation: CGU obligatoire
    if (!acceptedCGU) {
      toast.error(i18n.language === 'fr' 
        ? 'Vous devez accepter les Conditions Générales d\'Utilisation' 
        : 'You must accept the Terms of Use');
      return;
    }

    // Validation: Charte obligatoire
    if (!acceptedCharter) {
      toast.error(i18n.language === 'fr' 
        ? 'Vous devez accepter la Charte' 
        : 'You must accept the Charter');
      return;
    }

    setLoading(true);
    
    try {
      // For guides, we don't pass guideType at registration anymore
      // They will choose it in their dashboard
      await register(name, email, password, role, role === 'explorer' ? 'pending' : '');
      toast.success(t('common.success'));
      navigate('/welcome');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Step 0: Choose Role with immersive backgrounds
  if (step === 0) {
    return (
      <main className="min-h-screen pt-16" data-testid="register-page">
        {/* Header */}
        <div className="text-center py-8 px-4">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl font-bold mb-3"
          >
            {i18n.language === 'fr' ? 'Rejoignez XPerience' : 'Join XPerience'}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground text-lg"
          >
            {i18n.language === 'fr' ? 'Choisissez votre aventure' : 'Choose your adventure'}
          </motion.p>
        </div>

        {/* Two Sections Side by Side */}
        <div className="grid md:grid-cols-2 min-h-[70vh]">
          {/* Traveler Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="relative group cursor-pointer overflow-hidden"
            onClick={() => handleRoleSelect('experimenter')}
            data-testid="choose-traveler"
          >
            {/* Background Image */}
            <div className="absolute inset-0">
              <img 
                src="https://images.unsplash.com/photo-1539635278303-d4002c07eae3?w=1200&q=80"
                alt="Happy travelers"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 group-hover:from-black/70 transition-all duration-300" />
            </div>
            
            {/* Content */}
            <div className="relative h-full flex flex-col items-center justify-end p-8 md:p-12 text-white min-h-[350px] md:min-h-[500px]">
              <motion.div 
                className="text-center"
                whileHover={{ scale: 1.02 }}
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/40 group-hover:bg-primary group-hover:border-primary transition-all duration-300">
                  <Compass className="w-10 h-10" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-3">
                  {i18n.language === 'fr' ? 'Je suis Voyageur' : "I'm a Traveler"}
                </h2>
                <p className="text-white/80 text-base md:text-lg mb-6 max-w-md">
                  {i18n.language === 'fr' 
                    ? 'Découvrez des expériences authentiques avec des guides locaux passionnés'
                    : 'Discover authentic experiences with passionate local guides'}
                </p>
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full group-hover:bg-primary transition-all duration-300">
                  <span className="font-medium">
                    {i18n.language === 'fr' ? 'Commencer l\'aventure' : 'Start the adventure'}
                  </span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Guide Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="relative group cursor-pointer overflow-hidden"
            onClick={() => handleRoleSelect('explorer')}
            data-testid="choose-guide"
          >
            {/* Background Image */}
            <div className="absolute inset-0">
              <img 
                src="https://images.unsplash.com/photo-1663726630589-ac53a66f59df?w=1200&q=80"
                alt="Happy guide"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 group-hover:from-black/70 transition-all duration-300" />
            </div>
            
            {/* Content */}
            <div className="relative h-full flex flex-col items-center justify-end p-8 md:p-12 text-white min-h-[350px] md:min-h-[500px]">
              <motion.div 
                className="text-center"
                whileHover={{ scale: 1.02 }}
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/40 group-hover:bg-orange-500 group-hover:border-orange-500 transition-all duration-300">
                  <Map className="w-10 h-10" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-3">
                  {i18n.language === 'fr' ? 'Je propose une expérience' : "I offer an experience"}
                </h2>
                <p className="text-white/80 text-base md:text-lg mb-4 max-w-md">
                  {i18n.language === 'fr' 
                    ? 'Guide, artisan, praticien bien-être, organisateur d\'événements...'
                    : 'Guide, artisan, wellness practitioner, event organizer...'}
                </p>
                <div className="flex flex-wrap justify-center gap-2 mb-6 text-sm">
                  <span className="px-3 py-1 bg-white/10 rounded-full">🧭 Visites</span>
                  <span className="px-3 py-1 bg-white/10 rounded-full">🎨 Ateliers</span>
                  <span className="px-3 py-1 bg-white/10 rounded-full">🧘 Bien-être</span>
                  <span className="px-3 py-1 bg-white/10 rounded-full">🎪 Événements</span>
                </div>
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full group-hover:bg-orange-500 transition-all duration-300">
                  <span className="font-medium">
                    {i18n.language === 'fr' ? 'Commencer' : 'Get started'}
                  </span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Links */}
        <div className="text-center py-6">
          <p className="text-muted-foreground">
            {t('auth.has_account')}{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              {t('nav.login')}
            </Link>
          </p>
        </div>
      </main>
    );
  }

  // Step 1: Registration Form
  return (
    <main className="min-h-screen relative" data-testid="register-page">
      {/* Full Background Image based on role */}
      <div className="fixed inset-0 -z-10">
        <img 
          src={role === 'explorer' 
            ? "https://images.unsplash.com/photo-1663726630589-ac53a66f59df?w=1920&q=80"
            : "https://images.unsplash.com/photo-1539635278303-d4002c07eae3?w=1920&q=80"
          }
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 ${
          role === 'explorer' 
            ? 'bg-gradient-to-br from-orange-900/70 via-orange-800/50 to-black/60' 
            : 'bg-gradient-to-br from-sky-900/70 via-sky-800/50 to-black/60'
        }`} />
      </div>

      <div className="relative min-h-screen flex flex-col items-center justify-center py-20 px-4">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => { setStep(0); setRole(''); setGuideType(''); }}
          className="absolute top-24 left-4 md:left-8 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          {i18n.language === 'fr' ? 'Retour' : 'Back'}
        </motion.button>

        {/* GUIDE REGISTRATION FORM */}
        {role === 'explorer' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md bg-white/95 backdrop-blur-md rounded-3xl p-8 shadow-2xl"
          >
            {/* Guide Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                <Map className="w-8 h-8 text-orange-600" />
              </div>
              <h1 className="text-2xl font-bold text-orange-800 mb-2">
                {i18n.language === 'fr' ? 'Devenez Guide XPerience' : 'Become an XPerience Guide'}
              </h1>
              <p className="text-orange-600 text-sm">
                {i18n.language === 'fr' ? 'Partagez votre passion avec le monde' : 'Share your passion with the world'}
              </p>
            </div>

            {/* Guide Benefits */}
            <div className="bg-white rounded-2xl p-4 mb-6 border border-orange-100">
              <p className="text-sm font-medium text-orange-800 mb-3">
                {i18n.language === 'fr' ? 'Ce que vous obtenez :' : 'What you get:'}
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {i18n.language === 'fr' ? 'Inscription 100% gratuite' : '100% free registration'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {i18n.language === 'fr' ? 'Page profil personnalisée' : 'Personalized profile page'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {i18n.language === 'fr' ? 'Expériences gratuites ou payantes' : 'Free or paid experiences'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {i18n.language === 'fr' ? 'Paiements sécurisés via Stripe' : 'Secure payments via Stripe'}
                </li>
              </ul>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-orange-800">{t('auth.name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-400" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 bg-white border-orange-200 focus:border-orange-400"
                    placeholder={i18n.language === 'fr' ? 'Votre nom complet' : 'Your full name'}
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-orange-800">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-white border-orange-200 focus:border-orange-400"
                    placeholder="guide@exemple.com"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-orange-800">{t('auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-white border-orange-200 focus:border-orange-400"
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-orange-800">
                  {i18n.language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`pl-10 bg-white border-orange-200 focus:border-orange-400 ${
                      confirmPassword && password !== confirmPassword ? 'border-red-500' : ''
                    }`}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-red-500 text-xs">
                    {i18n.language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match'}
                  </p>
                )}
              </div>

              {/* CGU et Charte obligatoires */}
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="acceptCGU"
                    checked={acceptedCGU}
                    onChange={(e) => setAcceptedCGU(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                    required
                  />
                  <label htmlFor="acceptCGU" className="text-sm text-gray-700">
                    {i18n.language === 'fr' ? (
                      <>J'accepte les <a href="/terms" target="_blank" className="text-orange-600 hover:underline font-medium">Conditions Générales d'Utilisation</a> *</>
                    ) : (
                      <>I accept the <a href="/terms" target="_blank" className="text-orange-600 hover:underline font-medium">Terms of Use</a> *</>
                    )}
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="acceptCharter"
                    checked={acceptedCharter}
                    onChange={(e) => setAcceptedCharter(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                    required
                  />
                  <label htmlFor="acceptCharter" className="text-sm text-gray-700">
                    {i18n.language === 'fr' ? (
                      <>J'accepte la <a href="/guide-charter" target="_blank" className="text-orange-600 hover:underline font-medium">Charte des Guides</a> *</>
                    ) : (
                      <>I accept the <a href="/guide-charter" target="_blank" className="text-orange-600 hover:underline font-medium">Guide Charter</a> *</>
                    )}
                  </label>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full py-6 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                disabled={loading}
                data-testid="register-btn"
              >
                {loading ? t('common.loading') : (i18n.language === 'fr' ? 'Créer mon compte guide' : 'Create my guide account')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-orange-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-orange-50 text-orange-600">{t('auth.or')}</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full py-6 bg-white border-orange-200 hover:bg-orange-50"
              onClick={loginWithGoogle}
              data-testid="google-register-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.google')}
            </Button>

            <p className="text-center mt-6 text-orange-600/70">
              {t('auth.has_account')}{' '}
              <Link to="/login" className="text-orange-600 font-medium hover:underline" data-testid="login-link">
                {t('nav.login')}
              </Link>
            </p>
          </motion.div>
        )}

        {/* TRAVELER REGISTRATION FORM */}
        {role === 'experimenter' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md bg-white/95 backdrop-blur-md rounded-3xl p-8 shadow-2xl"
          >
            {/* Traveler Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-sky-100 flex items-center justify-center">
                <Compass className="w-8 h-8 text-sky-600" />
              </div>
              <h1 className="text-2xl font-bold text-sky-800 mb-2">
                {i18n.language === 'fr' ? 'Rejoignez les Voyageurs' : 'Join the Travelers'}
              </h1>
              <p className="text-sky-600 text-sm">
                {i18n.language === 'fr' ? 'Découvrez des expériences authentiques' : 'Discover authentic experiences'}
              </p>
            </div>

            {/* Traveler Benefits */}
            <div className="bg-white rounded-2xl p-4 mb-6 border border-sky-100">
              <p className="text-sm font-medium text-sky-800 mb-3">
                {i18n.language === 'fr' ? 'Ce qui vous attend :' : 'What awaits you:'}
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {i18n.language === 'fr' ? 'Accès à des centaines d\'expériences' : 'Access to hundreds of experiences'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {i18n.language === 'fr' ? 'Guides locaux vérifiés' : 'Verified local guides'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {i18n.language === 'fr' ? 'Réservation et paiement sécurisés' : 'Secure booking and payment'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {i18n.language === 'fr' ? 'Avis authentiques de voyageurs' : 'Authentic traveler reviews'}
                </li>
              </ul>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sky-800">{t('auth.name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-sky-400" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 bg-white border-sky-200 focus:border-sky-400"
                    placeholder={i18n.language === 'fr' ? 'Votre nom' : 'Your name'}
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sky-800">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-sky-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-white border-sky-200 focus:border-sky-400"
                    placeholder="voyageur@exemple.com"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sky-800">{t('auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-sky-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-white border-sky-200 focus:border-sky-400"
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sky-800">
                  {i18n.language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-sky-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`pl-10 bg-white border-sky-200 focus:border-sky-400 ${
                      confirmPassword && password !== confirmPassword ? 'border-red-500' : ''
                    }`}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-red-500 text-xs">
                    {i18n.language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match'}
                  </p>
                )}
              </div>

              {/* CGU et Charte obligatoires */}
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="acceptCGU-traveler"
                    checked={acceptedCGU}
                    onChange={(e) => setAcceptedCGU(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-sky-300 text-sky-600 focus:ring-sky-500"
                    required
                  />
                  <label htmlFor="acceptCGU-traveler" className="text-sm text-gray-700">
                    {i18n.language === 'fr' ? (
                      <>J'accepte les <a href="/terms" target="_blank" className="text-sky-600 hover:underline font-medium">Conditions Générales d'Utilisation</a> *</>
                    ) : (
                      <>I accept the <a href="/terms" target="_blank" className="text-sky-600 hover:underline font-medium">Terms of Use</a> *</>
                    )}
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="acceptCharter-traveler"
                    checked={acceptedCharter}
                    onChange={(e) => setAcceptedCharter(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-sky-300 text-sky-600 focus:ring-sky-500"
                    required
                  />
                  <label htmlFor="acceptCharter-traveler" className="text-sm text-gray-700">
                    {i18n.language === 'fr' ? (
                      <>J'accepte la <a href="/traveler-charter" target="_blank" className="text-sky-600 hover:underline font-medium">Charte des Voyageurs</a> *</>
                    ) : (
                      <>I accept the <a href="/traveler-charter" target="_blank" className="text-sky-600 hover:underline font-medium">Traveler Charter</a> *</>
                    )}
                  </label>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full py-6 bg-sky-500 hover:bg-sky-600 text-white font-semibold"
                disabled={loading}
                data-testid="register-btn"
              >
                {loading ? t('common.loading') : (i18n.language === 'fr' ? 'Commencer l\'aventure' : 'Start the adventure')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-sky-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-sky-50 text-sky-600">{t('auth.or')}</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full py-6 bg-white border-sky-200 hover:bg-sky-50"
              onClick={loginWithGoogle}
              data-testid="google-register-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.google')}
            </Button>

            <p className="text-center mt-6 text-sky-600/70">
              {t('auth.has_account')}{' '}
              <Link to="/login" className="text-sky-600 font-medium hover:underline" data-testid="login-link">
                {t('nav.login')}
              </Link>
            </p>
          </motion.div>
        )}
      </div>
    </main>
  );
};

export { LoginPage, RegisterPage };
